// ---------------------------------------------------------------------------
// legalDocs.ts — the eleven public legal documents, defined ONCE.
//
// Two surfaces need the same three facts about each document (its slug, its
// title and a one-line description of it): the Legal Hub renders them as cards,
// and config/seo.ts turns them into a per-page <title>/description and a
// sitemap entry. They used to exist only inside LegalHubPage, so the SEO work
// would have had to copy them — and a second copy of a text is BP-47's shape:
// two texts drift, one of them ends up wrong, and neither says which.
//
// EVERY docType HERE MUST RESOLVE. This list is hand-written and the backend
// does not validate it, so an entry can advertise a policy that 404s — which
// "Domain Dispute Policy" did from the day the hub shipped until 2026-08-18.
// The authority is `POLICY_DOC_TYPES` in backend/src/models/PolicyDocument.ts;
// cross-check it before adding one.
//
// The TITLES here are fallbacks. The hub replaces them with the backend's own
// labels once /policies/labels answers, so the cards cannot drift from the
// documents. The SEO layer deliberately does NOT wait for that call: a <title>
// has to be right in the server's first response for a crawler that does not
// re-read the head, and these eleven names have not changed since they were
// written.
// ---------------------------------------------------------------------------

export interface LegalDoc {
  /** URL slug — /legal/<docType>. */
  docType: string;
  /** Fallback title; the hub prefers the backend's label when it arrives. */
  title: string;
  /** One line, used on the hub card AND as the page's meta description. */
  description: string;
}

/** Agreements a merchant is actually bound by. Linked from the site footer. */
export const CORE_LEGAL_DOCS: LegalDoc[] = [
  { docType: 'tos',     title: 'Terms of Service',            description: 'The agreement that governs your use of the platform.' },
  { docType: 'privacy', title: 'Privacy Policy',              description: 'How we collect, use and protect personal information under POPIA.' },
  { docType: 'aup',     title: 'Acceptable Use Policy',       description: 'What you may and may not do when using the platform.' },
  { docType: 'sla',     title: 'Service Level Agreement',     description: 'Our availability commitment, service credits and support levels.' },
  { docType: 'refund',  title: 'Refund & Cancellation Policy', description: 'When fees are refundable and how cancellation works.' },
  { docType: 'cookie',  title: 'Cookie Policy',               description: 'The cookies we use and how to manage your choices.' },
  { docType: 'dpa',     title: 'Data Processing Agreement',   description: 'How we process your customers’ data on your behalf, for merchants.' },
];

/** Reachable only from the hub — the Stripe/Shopify pattern. */
export const ADDITIONAL_LEGAL_DOCS: LegalDoc[] = [
  { docType: 'abuse',      title: 'Abuse Policy',                                description: 'How to report abuse and how we respond to it.' },
  { docType: 'disclosure', title: 'Law-Enforcement & Data-Disclosure Policy',    description: 'How we respond to requests from authorities for information.' },
  { docType: 'security',   title: 'Responsible Disclosure Policy',               description: 'How to report a security vulnerability to us safely.' },
  { docType: 'paia',       title: 'PAIA Manual',                                 description: 'Our manual on access to records under PAIA.' },
];

export const ALL_LEGAL_DOCS: LegalDoc[] = [...CORE_LEGAL_DOCS, ...ADDITIONAL_LEGAL_DOCS];

/** Look one up by slug. Returns undefined for a docType this build knows nothing about. */
export function legalDocBySlug(docType: string | undefined): LegalDoc | undefined {
  return docType ? ALL_LEGAL_DOCS.find((d) => d.docType === docType) : undefined;
}
